import { bookActions } from '@/actions/bookActions';
import { isBookable, serviceIsExtraTimeAddOn } from '@/helpers';
import { getPickedEmployee } from '@/helpers/employee';
import { BookState, ExtraTimeRequirement, EXTRA_TIME_REQUIREMENT } from '@/types/state/book';
import { Place, PlaceEmployee, PlaceService, PlaceServiceGroup } from '@/types/state/shared';
import { useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Dispatch } from 'redux';
import { BookAppointmentContext } from './BookAppointment.hooks';
import { ModalView, UrlState, View } from './BookAppointment.types';
import { ANY_AMPLOYEE } from './BookAppointments.constants';

export const ScrollToTopElement = () => {
  const elementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleScrollToDiv() {
      if (!elementRef.current) {
        setTimeout(handleScrollToDiv, 10);
        return;
      }
      elementRef.current.scrollIntoView({ behavior: 'auto', block: 'start' });
    }
    handleScrollToDiv();
  }, []);

  return <div className="absolute left-0 top-0" ref={elementRef} />;
};

export function getScreenView(
  bookState: BookState,
  place: Place,
  previousView?: View,
  extraTimeRequiredForUser?: ExtraTimeRequirement,
): () => ModalView {
  return () => {
    const hideEmployee = bookState.place?.about?.settings?.hideEmployees ?? false;
    const hasPickedEmployee = bookState.employee ?? false;
    const hasPickedService = (bookState.services ?? []).length > 0;

    const current = (() => {
      if (!hasPickedService && !hasPickedEmployee) {
        return undefined;
      }

      if (!hasPickedService) {
        return 'pickService';
      }

      if (!hideEmployee && !hasPickedEmployee && hasMultipleEmployees(place)) {
        return 'pickEmployee';
      }

      const shouldShowExtraTime = showExtraTimeView(
        bookState.services ?? [],
        getPickedEmployee(bookState.employee, place?.employees ?? []) || ANY_AMPLOYEE,
        bookState.extraTimeRequirement || extraTimeRequiredForUser,
      );
      if (shouldShowExtraTime) {
        return 'pickExtraTime';
      }

      return 'summary';
    })();

    return {
      current,
      previous: previousView,
    };
  };
}

function shouldCheckForExtraTime(pickedServices: PlaceService[], pickedEmployee: PlaceEmployee | undefined) {
  const extraTimeAddOn = pickedServices.find(
    (service) =>
      serviceIsExtraTimeAddOn(service) ||
      (service?.extra?.addOnServices || []).find((item) => serviceIsExtraTimeAddOn(item)),
  );

  return !!extraTimeAddOn?.id;
}

export function showExtraTimeView(
  pickedServices: PlaceService[],
  pickedEmployee: PlaceEmployee | undefined,
  extraTimeRequiredForUser: ExtraTimeRequirement,
) {
  return (
    extraTimeRequiredForUser === EXTRA_TIME_REQUIREMENT.NotAnsweredYet &&
    shouldCheckForExtraTime(pickedServices, pickedEmployee)
  );
}

export function getNewExtraTimeAddOns(pickedServices: PlaceService[], pickedEmployee: PlaceEmployee | undefined) {
  let toAdd = [];
  const [pickedExtraTimeAddonServices, pickedMainServices] = [
    (pickedServices || []).filter((service) => serviceIsExtraTimeAddOn(service)),
    (pickedServices || []).filter((service) => !service?.about?.settings?.isAddOn),
  ];

  pickedMainServices.forEach((service) => {
    const extraTimeAddOn = (service?.extra?.addOnServices || []).find(
      (item) => serviceIsExtraTimeAddOn(item) && !pickedExtraTimeAddonServices.find((s) => s.id === item.id),
    );
    if (extraTimeAddOn) {
      toAdd.push(extraTimeAddOn);
    }
  });
  return toAdd;
}

// getPickedEmployee now lives in @/helpers/employee; re-exported here for existing BookAppointment imports.
export { getPickedEmployee };

export function getRecommendedServices(pickedServices: PlaceService[], pickedEmployee: PlaceEmployee | undefined) {
  // if no employee is picked return empty array
  if (!pickedEmployee) return [];

  // finds the last service from pickedServices that is not marked as an add-on
  const mainService =
    pickedServices
      .slice()
      .reverse()
      .find((service) => !service?.about?.settings?.isAddOn) || {};
  // all add-on services for the main service
  const addOnServices = mainService?.extra?.addOnServices || [];
  return (
    addOnServices
      // filter out services that are already picked
      .filter((service) => !pickedServices.some((pickedService) => pickedService.id === service.id))
      // filter out services that are not performed by the picked employee
      .filter((service) => pickedEmployee.services?.includes(service.id) || pickedEmployee.id === ANY_AMPLOYEE.id)
      // filter out services with extra time
      .filter((service) => !serviceIsExtraTimeAddOn(service))
      // sort by name alphabetically
      .sort((a, b) => ('' + a.name).localeCompare(b.name))
  );
}

export function filterBookPlaceServicesBySelectedEmployee(
  serviceCategories: PlaceServiceGroup[],
  pickedEmployee: PlaceEmployee,
): PlaceServiceGroup[] {
  return serviceCategories.reduce((acc, category) => {
    const servicesInCategoryPerformedByEmployee = category.services.filter((service) => {
      return pickedEmployee.services?.includes(service.id);
    });

    if (servicesInCategoryPerformedByEmployee.length) {
      acc.push({
        ...category,
        services: servicesInCategoryPerformedByEmployee,
      });
    }
    return acc;
  }, []);
}

export function filterBookPlaceServicesByEmptyCategories(serviceCategories: PlaceServiceGroup[]): PlaceServiceGroup[] {
  return serviceCategories.filter((category) => category.services?.length);
}

export function filterBookPlaceServicesByPickedServices(
  serviceCategories: PlaceServiceGroup[],
  pickedServices: PlaceService[],
): PlaceServiceGroup[] {
  return serviceCategories.map((category) => ({
    ...category,
    services: (category?.services || []).filter((service) =>
      pickedServices.every((pickedService) => pickedService.id !== service.id),
    ),
  }));
}

export function hasMultipleEmployees(place: Place): boolean {
  let count = 0;
  for (const employee of place?.employees ?? []) {
    if (isBookable({ employee, place })) {
      count++;
      if (count > 1) break;
    }
  }

  return count > 1;
}

export function syncBookstateWithUrl(
  urlState: UrlState,
  bookstate: BookState,
  place: Place,
  dispatch: Dispatch<any>,
  context: Pick<BookAppointmentContext, 'updateView' | 'close' | 'open' | 'view'>,
) {
  let pickedEmployee: PlaceEmployee | undefined;
  let pickedServices: PlaceService[] = [];
  let pickedAddons: PlaceService[] = [];

  /**
   * Selected employee
   */
  if (urlState.sr) {
    pickedEmployee = (place?.employees || []).find((e) => e.about?.externalId === urlState.sr);
  }

  /**
   * Selected main services
   */
  if (urlState.ss) {
    const serviceIds = urlState.ss.split(',').map((id) => parseInt(id, 10));
    pickedServices = (place?.services || [])
      .flatMap((category) => category.services)
      .filter((service) => serviceIds.includes(service.id));
  }

  /**
   * Picked addon services
   */
  if (urlState.rs) {
    const recommendedServices = getRecommendedServices(
      pickedServices || [],
      getPickedEmployee(bookstate.employee, place.employees) || ANY_AMPLOYEE,
    );

    pickedAddons = recommendedServices.filter((service) => {
      return urlState.rs
        ?.split(',')
        .map((id) => parseInt(id, 10))
        .includes(service.id);
    });
  }

  if (pickedEmployee) {
    dispatch(bookActions.pickEmployee(pickedEmployee.id, pickedEmployee.about.priceListId));
    dispatch(bookActions.keepEmployee());
    dispatch(bookActions.addPlace(place));
  }

  if (pickedServices.length) {
    bookstate.services?.forEach((service) => {
      dispatch(
        bookActions.popService(
          service.id,
          bookstate?.time?.timestamp,
          bookstate.services ?? [],
          bookstate.employee,
          place,
          true,
        ),
      );
    });
    pickedServices.forEach((service) => {
      dispatch(bookActions.addService(service, place));
    });
    pickedAddons.forEach((service) => {
      dispatch(bookActions.appendExtraService(service, pickedServices));
    });

    if (!pickedEmployee) {
      dispatch(bookActions.removeEmployee());
      dispatch(bookActions.addPlace(place));
    }
  }

  let view: View = undefined;

  //@TODO extraTime
  if (pickedServices.length && pickedEmployee) {
    view = 'summary';
  } else if (pickedServices.length && !pickedEmployee) {
    view = 'pickEmployee';
  } else if (!pickedServices.length && pickedEmployee) {
    view = 'pickServices';
  }

  if (view !== context.view.current) {
    context.updateView(view);
  }

  if (!view) {
    context.close();
    return;
  }

  context.open();
}

export function setUrlState(
  updated: Partial<UrlState>,
  history: ReturnType<typeof useHistory>,
  location: ReturnType<typeof useLocation>,
) {
  const urlState = getUrlParameters();

  // Treat null ("delete this param") and undefined ("param not present") as equal so the
  // no-op clear-all path (close()) returns early instead of triggering an empty replace.
  const same = (a: string | null | undefined, b: string | null | undefined) => (a ?? null) === (b ?? null);
  const unchanged = same(updated.sr, urlState.sr) && same(updated.ss, urlState.ss) && same(updated.rs, urlState.rs);
  if (unchanged) return;

  const params = new URLSearchParams(window.location.search);

  if (updated.sr === null) {
    params.delete('sr');
  }

  if (updated.ss === null) {
    params.delete('ss');
  }

  if (updated.rs === null) {
    params.delete('rs');
  }

  if (updated.sr) params.set('sr', updated.sr);
  if (updated.ss) params.set('ss', updated.ss);
  if (updated.rs) params.set('rs', updated.rs);

  history.replace({
    search: `?${params.toString()}`,
    hash: window.location.hash,
    state: location.state,
  });
}
export function getUrlParameters(search?: string): UrlState {
  const params = new URLSearchParams(search ?? window.location.search);
  return {
    sr: params.get('sr') ?? undefined,
    ss: params.get('ss') ?? undefined,
    rs: params.get('rs') ?? undefined,
  };
}
